// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Compact inline version dropdown for use within pipeline cards.
 * Fetches versions on open, shows selected version with chevron.
 */
import { useCallback, useEffect, useState, type FC } from 'react'
import { Loader2, ChevronDown, Check } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui'

import type { DocumentVersion } from '@/subsystems/documents'
import type { NodeApiClient } from '@/app-shell/services'

interface DocumentDetail {
  documentId: string
  latestVersion: number
  versions: DocumentVersion[]
}

interface InlineVersionSelectProps {
  documentId: string
  latestVersion: number
  selectedVersion: number
  basePath: string | null
  apiClient: NodeApiClient
  onVersionChange: (version: number) => void
  /** Optional: exclude this version from the list (for comparison dropdowns) */
  excludeVersion?: number
}

export const InlineVersionSelect: FC<InlineVersionSelectProps> = ({
  documentId,
  latestVersion,
  selectedVersion,
  basePath,
  apiClient,
  onVersionChange,
  excludeVersion,
}) => {
  const [open, setOpen] = useState(false)
  const [versions, setVersions] = useState<DocumentVersion[] | null>(null)
  const [loading, setLoading] = useState(false)

  // Fetch versions when popover opens
  useEffect(() => {
    if (!open || !basePath || versions !== null) return

    const fetchVersions = async () => {
      setLoading(true)
      try {
        const { data } = await apiClient.get<DocumentDetail>(
          `${basePath}/documents/${documentId}`
        )
        setVersions(data.versions.sort((a, b) => b.version - a.version))
      } catch {
        setVersions([])
      } finally {
        setLoading(false)
      }
    }
    void fetchVersions()
  }, [apiClient, basePath, documentId, open, versions])

  // Reset versions cache when documentId changes
  useEffect(() => {
    setVersions(null)
  }, [documentId])

  const handleSelect = useCallback((version: number) => {
    onVersionChange(version)
    setOpen(false)
  }, [onVersionChange])

  // Filter out excluded version if specified
  const filteredVersions = versions?.filter(v => v.version !== excludeVersion)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium text-muted-foreground bg-muted/50 hover:bg-muted rounded transition-colors duration-150"
        >
          v{selectedVersion}
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
          <span className="sr-only">Select version</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-32 p-1" align="start">
        {loading ? (
          <div className="flex items-center justify-center py-3">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-0.5">
            {filteredVersions?.map((v) => {
              const isSelected = v.version === selectedVersion
              return (
                <button
                  key={v.version}
                  onClick={() => handleSelect(v.version)}
                  className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left transition-colors duration-150 ${
                    isSelected ? 'bg-muted' : 'hover:bg-muted/50'
                  }`}
                >
                  <span className="text-sm font-medium text-foreground">
                    v{v.version}
                    {v.version === latestVersion && (
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">latest</span>
                    )}
                  </span>
                  {isSelected && <Check className="h-4 w-4 text-muted-foreground" />}
                </button>
              )
            })}
            {filteredVersions?.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                No other versions
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
